import type { Icon } from '@icon-park/react/lib/runtime';
import { noop } from 'lodash';
import { memo, useCallback } from 'react';

import type { Upload, UploadWithoutHref } from '@didaskill/domain/uploads/types';

import { FileBox } from '../file-box/FileBox';
import { useDownloadingState } from './useDownloadingState';

export type FileDownloadProps<TUpload extends Upload | UploadWithoutHref> = {
  /** File to download. */
  file: TUpload;
  /** Icon displayed instead of the mime-type default. */
  icon?: Icon;
  /** Callback when downloading the file. */
  onDownload?: (file: TUpload) => void | Promise<void>;
  /** Is the download button disabled. */
  disabled?: boolean;
  /** Display faded download links not to catch user attention. */
  isFaded?: boolean;
  onRemove?: (file: TUpload) => void;
  isLoading?: boolean;
};

export const FileDownloadBase = <TUpload extends Upload | UploadWithoutHref = Upload>({
  file,
  icon,
  disabled = false,
  isFaded = false,
  isLoading = false,
  onRemove = undefined,
  onDownload = noop,
}: FileDownloadProps<TUpload>) => {
  const onRemoveProxy = useCallback(() => onRemove(file), [file, onRemove]);

  const { isDownloading, onDownloadProxy } = useDownloadingState(() => onDownload(file), disabled);

  return (
    <FileBox
      fileName={file.fileName}
      mimeType={file.mimeType}
      icon={icon}
      isLoading={isLoading}
      isFaded={isFaded}
      isDisabled={disabled}
      isDownloading={isDownloading}
      onDownload={onDownloadProxy}
      onRemove={!!onRemove && onRemoveProxy}
    />
  );
};

FileDownloadBase.displayName = 'FileDownload';

// Cast type to keep the generic on FileDownload (otherwise it is lost after wrapping with memo).
export const FileDownload = memo(FileDownloadBase) as unknown as typeof FileDownloadBase;
